// src/components/admin/ProductManagementModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Package, Edit, Trash2, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { request } from "../../services/http";
import { productsService } from "../../services/products.service";

// Base URL for serving uploaded images (no /api suffix)
const IMAGE_BASE = (process.env.REACT_APP_API_URL || "/api")
  .replace(/\/+$/, "")
  .replace(/\/api$/i, "");

const ProductManagementModal = ({ onClose, onRefresh }) => {
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    productName: "",
    description: "",
    productUrl: "",
    isActive: true,
  });

  const [productImage, setProductImage] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const showMessage = (type, text) => {
    clearTimer();
    setMessage({ type, text });
    timerRef.current = setTimeout(() => setMessage({ type: "", text: "" }), 3500);
  };

  useEffect(() => {
    return () => clearTimer();
  }, []);

  const fetchProducts = async () => {
    setLoadingList(true);
    try {
      const data = await productsService.listAdmin();
      const list = Array.isArray(data) ? data : data?.data || data?.items || [];
      setProducts(list);
    } catch (e) {
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditingProduct(null);
    setShowForm(false);
    setFormData({ productName: "", description: "", productUrl: "", isActive: true });
    setProductImage(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
    setFormData({
      productName: product?.productName || "",
      description: product?.description || "",
      productUrl: product?.productUrl || "",
      isActive: typeof product?.isActive === "boolean" ? product.isActive : true,
    });
    setProductImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.productName?.trim() || !formData.productUrl?.trim()) {
      showMessage("error", t("validation.requiredFields"));
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("ProductName", formData.productName.trim());
      fd.append("Description", formData.description || "");
      fd.append("ProductUrl", formData.productUrl.trim());

      if (editingProduct) {
        fd.append("IsActive", String(!!formData.isActive));
      }

      if (productImage) {
        fd.append("productImage", productImage);
      }

      const path = editingProduct
        ? `/Products/${editingProduct.id}`
        : "/Products";
      const method = editingProduct ? "PUT" : "POST";

      await request(path, { method, body: fd });

      showMessage(
        "success",
        editingProduct
          ? t("admin.productMessages.updateSuccess")
          : t("admin.productMessages.addSuccess")
      );

      resetForm();
      await fetchProducts();
      await onRefresh?.();
    } catch (e) {
      showMessage("error", e?.message || t("admin.productMessages.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.productMessages.deleteConfirm"))) return;

    setSaving(true);
    try {
      await request(`/Products/${id}`, { method: "DELETE" });
      showMessage("success", t("admin.productMessages.deleteSuccess"));
      await fetchProducts();
      await onRefresh?.();
    } catch (e) {
      showMessage("error", e?.message || t("admin.productMessages.deleteError"));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = useMemo(
    () => !!formData.productName?.trim() && !!formData.productUrl?.trim() && !saving,
    [formData.productName, formData.productUrl, saving]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.3)] rounded-2xl overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-pink-600 to-purple-700 flex items-center gap-3">
          <button
            onClick={onClose}
            className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full
                       bg-black/40 hover:bg-black/70 border border-white/30 text-white transition"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-2xl font-bold text-white flex-1">
            {t("admin.productManagement.title")}
          </h2>

          <button
            onClick={fetchProducts}
            disabled={loadingList || saving}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white
                       flex items-center gap-2 disabled:opacity-40 flex-shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            {t("common.refresh")}
          </button>
        </div>

        <div className="p-6 text-white overflow-y-auto max-h-[calc(90vh-90px)]">
          {message.text && (
            <div
              className={`mb-5 p-3 rounded-lg text-center text-sm ${
                message.type === "success"
                  ? "bg-green-500/20 border border-green-400 text-green-200"
                  : "bg-red-500/20 border border-red-400 text-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Form */}
          {showForm ? (
            <div className="mb-6 p-6 bg-white/10 border border-white/10 rounded-xl backdrop-blur-lg shadow-inner">
              <h3 className="text-xl font-semibold mb-4">
                {editingProduct
                  ? t("admin.productManagement.editTitle")
                  : t("admin.productManagement.addTitle")}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-200">
                    {t("admin.productForm.nameLabel")}
                  </label>
                  <input
                    type="text"
                    value={formData.productName}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, productName: e.target.value }))
                    }
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:ring-2 focus:ring-pink-500 outline-none"
                    placeholder={t("admin.productForm.namePlaceholder")}
                    required
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-200">
                    {t("admin.productForm.descriptionLabel")}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, description: e.target.value }))
                    }
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:ring-2 focus:ring-pink-500 outline-none"
                    rows={3}
                    placeholder={t("admin.productForm.descriptionPlaceholder")}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-200">
                    {t("admin.productForm.urlLabel")}
                  </label>
                  <input
                    type="url"
                    value={formData.productUrl}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, productUrl: e.target.value }))
                    }
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:ring-2 focus:ring-pink-500 outline-none"
                    placeholder={t("admin.productForm.urlPlaceholder")}
                    required
                  />
                </div>

                {editingProduct && (
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!formData.isActive}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, isActive: e.target.checked }))
                      }
                      className="w-5 h-5"
                    />
                    <span className="text-sm text-gray-200">
                      {t("admin.productForm.activeLabel")}
                    </span>
                  </label>
                )}

                <div>
                  <label className="text-sm text-gray-200">
                    {t("admin.productForm.imageLabel")}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProductImage(e.target.files?.[0] || null)}
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-sm text-gray-300"
                  />

                  {/* Preview existing image in edit mode */}
                  {editingProduct?.productImage && !productImage && (
                    <img
                      src={`${IMAGE_BASE}${editingProduct.productImage}`}
                      alt={editingProduct.productName}
                      className="mt-2 h-20 rounded-lg object-cover border border-white/20"
                    />
                  )}

                  {productImage && (
                    <p className="text-xs text-green-300 mt-1">
                      {t("messages.fileSelected", { name: productImage.name })}
                    </p>
                  )}
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 py-2 rounded-lg font-semibold shadow-lg hover:opacity-90 disabled:opacity-40"
                  >
                    {saving
                      ? t("admin.productForm.saving")
                      : editingProduct
                      ? t("admin.productForm.update")
                      : t("admin.productForm.save")}
                  </button>

                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 bg-gray-600 py-2 rounded-lg hover:bg-gray-700 font-semibold"
                    disabled={saving}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <button
              onClick={openCreate}
              className="mb-6 w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-red-500 font-semibold shadow-md hover:opacity-90 flex items-center justify-center"
            >
              <Plus className="w-5 h-5 ml-2" />
              {t("admin.productManagement.addNewButton")}
            </button>
          )}

          {/* List */}
          {loadingList ? (
            <div className="py-10 text-center text-gray-200">
              {t("common.loading")}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-lg shadow-xl"
                >
                  {product.productImage ? (
                    <img
                      src={`${IMAGE_BASE}${product.productImage}`}
                      alt={product.productName}
                      className="w-full h-36 object-cover rounded-lg mb-3"
                    />
                  ) : (
                    <div className="w-full h-36 bg-white/10 rounded-lg mb-3 flex items-center justify-center">
                      <Package className="w-12 h-12 text-gray-300" />
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-bold mb-1 text-pink-200">
                      {product.productName}
                    </h3>

                    {typeof product.isActive === "boolean" && (
                      <span
                        className={`px-2 py-1 rounded text-xs border ${
                          product.isActive
                            ? "bg-green-500/15 border-green-400/40 text-green-200"
                            : "bg-gray-500/15 border-gray-400/40 text-gray-200"
                        }`}
                      >
                        {product.isActive ? t("common.active") : t("common.inactive")}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-300 line-clamp-2 mb-2">
                    {product.description}
                  </p>

                  <p className="text-xs text-blue-300 truncate mb-3">
                    {product.productUrl}
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => openEdit(product)}
                      className="flex-1 bg-blue-600 py-2 rounded-lg hover:bg-blue-700 font-semibold text-white text-sm flex justify-center items-center disabled:opacity-50"
                      disabled={saving}
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      {t("common.edit")}
                    </button>

                    <button
                      onClick={() => handleDelete(product.id)}
                      disabled={saving}
                      className="flex-1 bg-red-600 py-2 rounded-lg hover:bg-red-700 font-semibold text-white text-sm disabled:opacity-50 flex justify-center items-center"
                    >
                      <Trash2 className="w-4 h-4 ml-1" />
                      {t("common.delete")}
                    </button>
                  </div>
                </div>
              ))}

              {products.length === 0 && (
                <div className="md:col-span-2 lg:col-span-3 text-center py-14 text-gray-200">
                  {t("admin.productManagement.noProducts")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductManagementModal;
